import { AutomationValue } from './ast/AutomationValue.js';
import { targetContractFromAction } from './serviceTargetContract.js';

/**
 * Resolve the entity inherited by a Designer node without writing it back to
 * the node or to a child binding/action.  The same resolver is used by the
 * editor and by the export boundary so inheritance has one definition.
 *
 * `configOf(node)` reads a node's Home Assistant configuration; the export
 * passes one that includes the page's flow graph (hass/hassEffective.js).
 */
export function getEffectivePrimaryEntity(node, options) {
  return getEffectivePrimaryEntityInfo(node, options).entityId;
}

export function getEffectivePrimaryEntityInfo(node, { configOf = item => item.hass_config } = {}) {
  let current = node;
  const visited = new Set();
  while (current && !visited.has(current)) {
    visited.add(current);
    const entity = configOf(current)?.primaryEntity;
    if (typeof entity === 'string' && entity.trim()) {
      return {
        entityId: entity.trim(),
        sourceNode: current,
        inherited: current !== node,
      };
    }
    current = current.parent ?? current.getParent?.() ?? null;
  }
  return { entityId: '', sourceNode: null, inherited: false };
}

export function primaryEntitySourceLabel(info) {
  if (!info?.sourceNode) return '';
  const source = info.sourceNode;
  const type = source.obj || source.type || 'object';
  const id = source.haspid ?? source.id;
  return id == null ? type : `${type} ${id}`;
}

function cloneWithPrototype(value) {
  const clone = Object.create(Object.getPrototypeOf(value));
  Object.assign(clone, value);
  return clone;
}

function valueAsString(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  return String(value.toString?.() ?? value).trim();
}

function hasEntityValue(value) {
  if (Array.isArray(value)) return value.some(hasEntityValue);
  return valueAsString(value) !== '';
}

function hasExplicitActionEntity(action) {
  if (hasEntityValue(action?.target?.entity_id)) return true;
  const parameter = action?.parameters?.find(item => item.key === 'entity_id');
  return hasEntityValue(parameter?.value);
}

function hasOtherExplicitTarget(action) {
  return Object.entries(action?.target ?? {}).some(([key, value]) =>
    key !== 'entity_id' && key !== 'source' && hasEntityValue(value));
}

function resolveBinding(binding, entityId) {
  if (binding?.strategy === 'switch') {
    // The switch and each of its case bindings inherit on their own.
    const resolved = cloneWithPrototype(binding);
    if (!hasEntityValue(binding.entityId)) resolved.entityId = entityId;
    resolved.cases = binding.cases.map(item => ({ ...item, binding: resolveBinding(item.binding, entityId) }));
    return resolved;
  }
  if (!binding || !['state', 'mapping'].includes(binding.strategy) || hasEntityValue(binding.entityId)) {
    return binding;
  }
  const resolved = cloneWithPrototype(binding);
  // Keep sourceType and all persisted fields intact. This is an export-only
  // effective value; the saved binding must continue to inherit in the UI.
  resolved.entityId = entityId;
  return resolved;
}

function resolveAction(action, entityId) {
  if (!action || action.type !== 'call_service') return action;

  const contract = targetContractFromAction(action);
  // Known targetless services and full service identifiers without metadata
  // must not receive an invented Primary Entity. Explicit targets remain on
  // the action so the export boundary can preserve them when the contract is
  // unknown, or omit them when the contract explicitly rejects targets.
  if (!contract.entity) return action;
  if (hasExplicitActionEntity(action) || hasOtherExplicitTarget(action)) return action;

  const resolved = cloneWithPrototype(action);
  resolved.parameters = (action.parameters ?? []).map(parameter => ({ ...parameter }));
  const parameter = resolved.parameters.find(item => item.key === 'entity_id');

  if (parameter) {
    parameter.value = AutomationValue.literal(entityId);
    return resolved;
  }

  if (action.target && typeof action.target === 'object') {
    resolved.target = { ...action.target, entity_id: entityId };
    delete resolved.target.source;
    return resolved;
  }

  resolved.parameters.push({ key: 'entity_id', value: AutomationValue.literal(entityId) });
  return resolved;
}

/**
 * Materialize inherited entity defaults for export only.
 *
 * The returned models are shallow clones only where a binding or action must
 * receive an effective entity. Original editor models and saved configuration
 * are never mutated, so inherited/explicit semantics remain visible after
 * export and undo/redo continue to work as before.
 */
export function materializePrimaryEntity(models, entityId) {
  const normalizedEntity = String(entityId ?? '').trim();
  if (!normalizedEntity) return models;

  return (models ?? []).map(model => {
    const originalBindings = model.bindings ?? [];
    const originalActions = model.actions ?? [];
    const bindings = originalBindings.map(binding => resolveBinding(binding, normalizedEntity));
    const actions = originalActions.map(action => resolveAction(action, normalizedEntity));
    const bindingsChanged = bindings.some((binding, index) => binding !== originalBindings[index]);
    const actionsChanged = actions.some((action, index) => action !== originalActions[index]);
    if (!bindingsChanged && !actionsChanged) return model;

    const resolvedModel = cloneWithPrototype(model);
    if (bindingsChanged) resolvedModel.bindings = bindings;
    if (actionsChanged) resolvedModel.actions = actions;
    return resolvedModel;
  });
}
